import * as React from 'react';

import { translate } from '~l10n/client';
import { groupBy } from 'underscore';

import { UIButton } from '~shared/components/buttons';
import { IconButton, SmallButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { useAutoFocus } from '~shared/components/helpers';
import {
  IconChevron,
  IconFolder,
  IconFolderAdd,
  IconUser,
} from '~shared/components/icons/standard';
import { Inline } from '~shared/components/Inline';
import TextInput from '~shared/components/TextInput';
import { toast } from '~shared/components/Toast';
import { Direction } from '~shared/utils/sort';
import { sortItemsByManyProperties } from '~shared/utils/sort/sortItemsByManyProperties';
import { QuizWorkspaceList } from '~teacher/components/QuizWorkspaceList';
import { ListItemProps } from '~teacher/components/QuizWorkspaceList/QuizWorkspaceListItem';
import { useSelectQuizFolder } from '~teacher/components/quizzes/CopyMoveDialog/useSelectQuizFolder';
import { useQuizzes } from '~teacher/hook';
import { useCreateFolder } from '~teacher/hook/useCreateFolder';
import { useMoveQuizFolder } from '~teacher/hook/useMoveQuizFolder';

import { FolderType } from '../../../../api/folders';
import { QuizItemCache } from '../../../../api/quiz/getQuizzes/normalize/toQuizItemCache';
import { matchUniqueIdToEntityId } from '../../../../api/quiz/getQuizzes/normalize/uniqueIds';
import { uniqueIdFactory } from '../../../../api/quiz/getQuizzes/normalize/uniqueIds';
import { Folder, QuizOrFolder } from '../../../../api/quiz/getQuizzes/types';

function getQuizTitle(itemsToMove: QuizOrFolder[]): string {
  if (!itemsToMove?.length) return '';
  if (itemsToMove?.length === 1) {
    const [itemToMove] = itemsToMove;
    return translate('Move {0}').format(itemToMove.name);
  }

  return translate('Move {0} items').format(itemsToMove.length);
}

function getSuccessToastMessage(itemsToMove: QuizOrFolder[]) {
  if (!itemsToMove.length) return '';
  if (itemsToMove?.length === 1) {
    const [itemToMove] = itemsToMove;
    return translate('{0} moved successfully').format(itemToMove.name);
  }

  return translate('{0} items moved successfully').format(itemsToMove.length);
}

function useOnMoveItems(
  itemsToMove: QuizOrFolder[],
  onClose: (success: boolean, newParentId: number) => void,
  isOpen: boolean
) {
  const [isMoveDisabled, setIsMoveDisabled] = React.useState(false);

  const [selectedParentId, setSelectedParentId] = React.useState<number>(null);
  const initialParentId = itemsToMove?.[0]?.parentId;

  React.useEffect(() => {
    if (isOpen && initialParentId != undefined) {
      setSelectedParentId(initialParentId);
    }
  }, [isOpen, initialParentId]);

  const handleSelectedFolder = React.useCallback(
    (itemId: string) => {
      let newParentId = null;
      if (itemId !== null) {
        const { entityId, itemType } = matchUniqueIdToEntityId(itemId);
        if (itemType === 'folder') {
          newParentId = entityId;
        }
      }

      setSelectedParentId(newParentId);
      if (itemsToMove) {
        let shouldDisable = false;
        for (const item of itemsToMove) {
          if (item.type === 'folder' && newParentId === item.id) {
            console.error('Cannot move a folder into itself');
            shouldDisable = true;
            break;
          }
        }
        setIsMoveDisabled(
          shouldDisable || itemsToMove[0].parentId === newParentId
        );
      }
    },
    [setSelectedParentId, itemsToMove]
  );

  const moveMutation = useMoveQuizFolder();
  const onMove = React.useCallback(() => {
    if (!itemsToMove) return;
    setIsMoveDisabled(true);
    const { quiz, folder } = groupBy(
      itemsToMove,
      (quizOrFolder) => quizOrFolder.type
    );

    const folderIds = (folder ?? []).map((folder) => folder.id);
    const quizIds = (quiz ?? []).map((quiz) => quiz.id);
    moveMutation.mutate(
      {
        quizIds: quizIds,
        folderIds: folderIds,
        parentId: selectedParentId,
      },
      {
        onSuccess: () => {
          toast(getSuccessToastMessage(itemsToMove), { tone: 'positive' });
          onClose(true, selectedParentId);
        },
        onError: (error) => {
          if (error instanceof Error) {
            toast(`Error: ${error.message}`, { tone: 'critical' });
          }
          setIsMoveDisabled(false);
        },
      }
    );
  }, [itemsToMove, selectedParentId, moveMutation, onClose]);

  return {
    moveMutation,
    onMove,
    selectedParentId,
    isMoveDisabled,
    handleSelectedFolder,
  };
}

export interface MoveDialogProps {
  itemIdsToMove: Set<string>;
  onClose: (success: boolean, newParentFolder?: number) => void;
  isOpen: boolean;
}

export function MoveDialog({
  itemIdsToMove,
  onClose,
  isOpen,
}: MoveDialogProps) {
  const select = useSelectQuizFolder(itemIdsToMove);
  const enabled = itemIdsToMove?.size >= 1;

  const itemsToMove = useQuizzes({
    select: select,
    enabled: enabled,
  });

  const quizTitle = React.useMemo(
    () => getQuizTitle(itemsToMove.data),
    [itemsToMove.data]
  );

  const { onMove, isMoveDisabled, handleSelectedFolder, selectedParentId } =
    useOnMoveItems(itemsToMove.data, onClose, isOpen);

  return (
    <Dialog
      onClose={() => onClose(false)}
      id="move-quiz-dialog"
      open={isOpen}
      title={quizTitle}
      closeLabel={translate('Close')}
    >
      <div className="mt-5 mb-5" />
      <div className="text-base leading-8">
        <TraversablePersonalQuizList
          selectedParentFolder={selectedParentId}
          onSelected={handleSelectedFolder}
          selectedItems={itemsToMove.isFetched ? itemsToMove.data : []}
        />
      </div>
      <div>
        <Divider />
        <div className="mt-8 pt-8">
          <Inline align="right">
            <UIButton onClick={onClose}>{translate('Cancel')}</UIButton>
            <UIButton
              appearance="primary"
              onClick={onMove}
              isDisabled={isMoveDisabled}
            >
              {translate('Move')}
            </UIButton>
          </Inline>
        </div>
      </div>
    </Dialog>
  );
}

interface QuizTraverseListProps {
  selectedParentFolder: number;
  onSelected: (item: string) => void;
  selectedItems?: QuizOrFolder[];
}

function getChildIds(
  quizItemCache: QuizItemCache,
  currentFolder: Folder
): string[] {
  if (currentFolder) {
    return currentFolder.children;
  }

  return quizItemCache.rootItems;
}

function getCurrentFolderAndListOfItems({
  selectedParentFolder,
  selectedFolders,
}: {
  selectedParentFolder: number;
  selectedFolders: Set<string>;
}): (quizItemCache: QuizItemCache) => {
  parentFolder: Folder;
  currentFolder: Folder;
  listOfItems: (QuizOrFolder & { isDisabled: boolean })[];
} {
  return (quizItemCache: QuizItemCache) => {
    const currentFolder = quizItemCache.itemById[
      uniqueIdFactory.folder.toUniqueId(selectedParentFolder)
    ] as Folder;
    const parentFolder = quizItemCache.itemById[
      uniqueIdFactory.folder.toUniqueId(currentFolder?.parentId)
    ] as Folder;

    const listOfItems = sortItemsByManyProperties({
      items: getChildIds(quizItemCache, currentFolder).map(
        (id) => quizItemCache.itemById[id]
      ),
      itemSorts: [
        { sortDirection: Direction.Descending, property: 'date' },
        { sortDirection: Direction.Ascending, property: 'type' },
      ],
    }).map((quizOrFolder): QuizOrFolder & { isDisabled: boolean } => ({
      ...quizOrFolder,
      isDisabled:
        quizOrFolder.type === 'quiz' ||
        selectedFolders.has(quizOrFolder.uniqueId),
    }));

    return { currentFolder, parentFolder, listOfItems };
  };
}

function QuizTraverseListHeader({
  currentFolder,
  parentFolder,
  closeNewFolder,
  goBack,
}: {
  currentFolder: Folder;
  parentFolder: Folder;
  closeNewFolder: () => void;
  goBack: () => void;
}) {
  if (currentFolder) {
    return (
      <div className="flex items-center h-16">
        <IconButton
          onClick={goBack}
          aria-label={translate('Back to {0}').format(
            parentFolder?.name ?? translate('Personal')
          )}
        >
          <IconChevron direction="left" />
        </IconButton>
        <span className="text-base font-800 leading-8 text-blue-900 w-full truncate mr-3">
          {currentFolder.name}
        </span>
        <IconButton
          onClick={
            closeNewFolder
            /*() => setOpenNewFolder((state) => !state)*/
          }
        >
          <IconFolderAdd className="text-cyan-700" />
        </IconButton>
      </div>
    );
  }

  return (
    <div className="flex items-center h-16 w-full">
      <div className="flex flex-none">
        <IconUser />
      </div>
      <div className="pl-6 text-base font-800 leading-8 text-blue-900 w-full truncate mr-3">
        {translate('Personal')}
      </div>
      <IconButton onClick={closeNewFolder}>
        <IconFolderAdd className="text-cyan-700" />
      </IconButton>
    </div>
  );
}

export function TraversablePersonalQuizList({
  selectedParentFolder,
  onSelected,
  selectedItems,
}: QuizTraverseListProps) {
  const selectedFolders = new Set(
    (selectedItems ?? [])
      .filter((si) => si.type === 'folder')
      .map((si) => si.uniqueId)
  );
  const quizzesList = useQuizzes({
    select: getCurrentFolderAndListOfItems({
      selectedParentFolder: selectedParentFolder,
      selectedFolders: selectedFolders,
    }),
  });

  if (quizzesList.data) {
    return (
      <MoveQuizFolderList
        currentFolder={quizzesList.data.currentFolder}
        parentFolder={quizzesList.data.parentFolder}
        listOfItems={quizzesList.data.listOfItems}
        onSelected={onSelected}
      />
    );
  }

  return <>Loading</>;
}

function MoveQuizFolderList({
  onSelected,
  currentFolder,
  parentFolder,
  listOfItems,
}: {
  currentFolder: Folder;
  parentFolder: Folder;
  listOfItems: (QuizOrFolder & { isDisabled: boolean })[];
  onSelected: (uniqueId: string) => void;
}) {
  const [openNewFolder, setOpenNewFolder] = React.useState<boolean>(false);

  const goBack = () => {
    onSelected(parentFolder?.uniqueId ?? null);
  };

  const handleClickMaster = (item: string) => {
    onSelected(item);
  };

  const listItems = listOfItems.map<ListItemProps>(
    ({ name, uniqueId, type, isDisabled }) => ({
      name,
      uniqueId,
      type,
      isDisabled,
      onClick: () => {
        setOpenNewFolder(false);
        handleClickMaster(uniqueId);
      },
    })
  );

  return (
    <>
      <Divider />
      <QuizTraverseListHeader
        currentFolder={currentFolder}
        parentFolder={parentFolder}
        closeNewFolder={() => setOpenNewFolder(false)}
        goBack={goBack}
      />
      <Divider />
      <div className={'h-80 overflow-y-scroll overflow-x-hidden'}>
        <QuizWorkspaceList
          items={listItems}
          header={
            <>
              {openNewFolder && (
                <NewFolder
                  setOpenNewFolder={setOpenNewFolder}
                  parent={currentFolder?.id ?? null}
                />
              )}
            </>
          }
        />
      </div>
    </>
  );
}

function useCreateNewFolder({
  parentId,
  onClose,
  newFolderName,
}: {
  parentId: number;
  onClose: () => void;
  newFolderName: string;
}) {
  const { mutate, isLoading } = useCreateFolder();
  const saveFolder = (folderName: string) => {
    mutate(
      {
        name: folderName,
        parentId: parentId,
        type: FolderType.QUIZ_FOLDER,
      },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (error) => {
          if (error instanceof Error) {
            toast(`Error: ${error.message}`, { tone: 'critical' });
          }
        },
      }
    );
  };

  const createNewFolder = () => saveFolder(newFolderName);

  return { createNewFolder, isLoading };
}

interface NewFolderProps {
  parent: number;
  setOpenNewFolder: (state: boolean) => void;
}

function NewFolder({ parent, setOpenNewFolder }: NewFolderProps) {
  const [name, setName] = React.useState<string>('');
  const { createNewFolder } = useCreateNewFolder({
    parentId: parent ? parent : null,
    onClose: () => {
      setOpenNewFolder(false);
    },
    newFolderName: name,
  });
  const autoFocus = useAutoFocus();
  return (
    <>
      <div className="w-full flex flex-row py-4 sticky top-0 bg-white z-101">
        <div className="flex items-center justify-start min-w-30 mr-3">
          <IconFolder />
        </div>
        <TextInput
          ref={autoFocus}
          aria-label={translate('Folder Name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Untitled Folder"
          className="w-full mr-2"
        />
        <SmallButton
          onClick={() => {
            createNewFolder();
          }}
        >
          {translate('Save')}
        </SmallButton>
      </div>
      <Divider />
    </>
  );
}
